import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, CustomInput, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getBankDetails, saveBankDetails } from '../../services/firestoreService';
import { isValidAccountNumber, isValidIFSC } from '../../utils/helpers';
import './BankDetailsScreen.css';

export const BankDetailsScreen = () => {
    const navigate = useNavigate();
    const alert = useAlert();

    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        loadBankDetails();
    }, []);

    const loadBankDetails = async () => {
        try {
            const data = await getBankDetails();
            if (data) {
                setAccountHolderName(data.accountHolderName || '');
                setBankName(data.bankName || '');
                setAccountNumber(data.accountNumber || '');
                setConfirmAccountNumber(data.accountNumber || '');
                setIfscCode(data.ifscCode || '');
            } else {
                setIsEditing(true);
            }
        } catch (error) {
            console.error('Error loading bank details:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!accountHolderName.trim()) {
            newErrors.accountHolderName = 'Account holder name is required';
        }

        if (!bankName.trim()) {
            newErrors.bankName = 'Bank name is required';
        }

        if (!accountNumber) {
            newErrors.accountNumber = 'Account number is required';
        } else if (!isValidAccountNumber(accountNumber)) {
            newErrors.accountNumber = 'Invalid account number (9-18 digits)';
        }

        if (accountNumber !== confirmAccountNumber) {
            newErrors.confirmAccountNumber = 'Account numbers do not match';
        }

        if (!ifscCode) {
            newErrors.ifscCode = 'IFSC code is required';
        } else if (!isValidIFSC(ifscCode)) {
            newErrors.ifscCode = 'Invalid IFSC code';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            await saveBankDetails(accountHolderName, accountNumber, ifscCode.toUpperCase(), bankName);
            alert.showSuccess('Success', 'Bank details saved successfully', () => navigate(-1));
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to save bank details');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading bank details..." />;
    }

    return (
        <div className="bank-details-screen">
            <LoadingOverlay visible={saving} message="Saving..." />

            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">Bank Details</h1>
                <div style={{ width: 40 }}></div>
            </div>

            <GlassCard className="info-card">
                <span className="material-icons info-icon">info</span>
                <p className="info-text">
                    Please ensure your bank details are correct.
                    Withdrawals will be processed to this account.
                </p>
            </GlassCard>

            <div className="form-section">
                <CustomInput
                    label="Account Holder Name"
                    placeholder="Enter name as per bank"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    leftIcon="person"
                    error={errors.accountHolderName}
                    disabled={!isEditing && accountHolderName}
                />

                <CustomInput
                    label="Bank Name"
                    placeholder="e.g., State Bank of India"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    leftIcon="account_balance"
                    error={errors.bankName}
                    disabled={!isEditing && bankName}
                />

                <CustomInput
                    label="Account Number"
                    placeholder="Enter account number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                    leftIcon="credit_card"
                    error={errors.accountNumber}
                    disabled={!isEditing && accountNumber}
                />

                <CustomInput
                    label="Confirm Account Number"
                    placeholder="Re-enter account number"
                    value={confirmAccountNumber}
                    onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))}
                    leftIcon="credit_card"
                    error={errors.confirmAccountNumber}
                    disabled={!isEditing && confirmAccountNumber}
                />

                <CustomInput
                    label="IFSC Code"
                    placeholder="e.g., SBIN0001234"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                    leftIcon="code"
                    error={errors.ifscCode}
                    disabled={!isEditing && ifscCode}
                />
            </div>

            {isEditing ? (
                <GradientButton
                    title="Save Bank Details"
                    onClick={handleSave}
                    loading={saving}
                />
            ) : (
                <GradientButton
                    title="Edit Bank Details"
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                />
            )}
        </div>
    );
};

export default BankDetailsScreen;
